import React, { useEffect, useRef } from 'react';
import { Loader } from '@dynamon-as/ui';
import type { EmployeeMessage } from '@shared/types';
import { ChatMessage } from '@features/requests/components/RequestChat/components/ChatMessage';
import styles from './ChatMessageList.module.css';

/**
 * Props for ChatMessageList component
 */
export interface ChatMessageListProps {
    messages: EmployeeMessage[];
    isLoading?: boolean;
}

/**
 * Renders a scrollable list of chat messages with loading and empty states
 */
export const ChatMessageList = React.memo(function ChatMessageList({
    messages,
    isLoading = false,
}: ChatMessageListProps): React.ReactElement {
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect((): void => {
        const el = scrollRef.current;

        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [messages.length]);

    if (isLoading) {
        return (
            <div
                className={styles.container}
                data-testid="chat-message-list"
                role="status"
                aria-live="polite"
            >
                <Loader
                    isLoading={true}
                    label="Laster inn meldinger..."
                />
            </div>
        );
    }

    if (messages.length === 0) {
        return (
            <div
                className={styles.container}
                data-testid="chat-message-list"
            >
                <div className={styles.empty}>
                    <p className={styles.emptyMessage}>Ingen meldinger enda</p>
                    <p className={styles.emptySubtext}>
                        Skriv en kommentar nedenfor
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            className={styles.container}
            data-testid="chat-message-list"
        >
            {messages.map((m): React.ReactElement => (
                <ChatMessage
                    key={m.id}
                    message={m}
                />
            ))}
            <div ref={scrollRef} />
        </div>
    );
});
